/**
 * UserIssuesTable: the User Analytics directory as a Carbon DataTable
 * (mockup 02 tables).
 *
 *   head      "User directory" over one line naming the count and the filter
 *   notices   info notifications that define the active filter's terms
 *   tabs      contained tabs per category (All users, Activity, Attention,
 *             Security) with count tags
 *   toolbar   the category's filter select, the "need attention" shortcut and
 *             the CSV export
 *   rows      user, status tags, sign-in, licenses, MFA strength and the issue
 *             (severity carried by shape and word; Coral only for critical)
 *   footer    server pagination ("Previous page" / "Next page")
 *
 * The component's external contract is unchanged: same props, same callbacks,
 * same filter/pagination/export behavior. `pageSize` is a new optional prop
 * (the page fetches 20 per page) so the pager can print the range.
 */

import { useMemo, useState, useCallback } from 'react'
import type { UserDetailItem, UserIssueFilter } from '@/types'
import { DEFAULT_INACTIVITY_THRESHOLD_DAYS } from '@/types'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { Button } from '@/components/carbon/Button'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconDownload } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tag } from '@/components/carbon/Tag'
import type { SeverityLevel } from '@/components/carbon/types'

interface Props {
  users: UserDetailItem[]
  total: number
  currentPage: number
  totalPages: number
  selectedFilter: UserIssueFilter
  onFilterChange: (filter: UserIssueFilter) => void
  onPageChange: (page: number) => void
  isLoading: boolean
  inactivityThresholdDays?: number
  tenantId?: string
  filterCounts?: Record<string, number>
  resourceAccountCount?: number
  /**
   * When true, the table view AND the CSV export narrow to Global Admins
   * only. Mirrors the `?scope=globalAdmins` URL contract honored by the
   * parent UserInsights page. Without this flag the export silently returns
   * the full unscoped population, breaking the "this download matches the
   * page I'm looking at" promise.
   */
  globalAdminsOnly?: boolean
  /** Rows per server page, for the pager's range ("1–20 of 49 people"). Default 20. */
  pageSize?: number
}

type TabKey = 'all' | 'activity' | 'attention' | 'security'

interface TabDefinition {
  key: TabKey
  label: string
  badgeKey?: string
  filters: { key: UserIssueFilter; label: string; description: string }[]
}

function createTabs(threshold: number): TabDefinition[] {
  return [
    {
      key: 'all',
      label: 'All users',
      badgeKey: 'All',
      filters: [
        { key: 'All', label: 'All', description: 'All users in the tenant' },
        { key: 'ActiveMembers', label: 'Active members', description: `Enabled member accounts with sign-in activity within ${threshold} days` },
        { key: 'ActiveGuests', label: 'Active guests', description: `Enabled guest accounts with sign-in activity within ${threshold} days` },
        { key: 'ResourceAccounts', label: 'Resource accounts', description: 'Room mailboxes, equipment, Bookings pages, Teams Phone and Rooms accounts' },
      ],
    },
    {
      key: 'activity',
      label: 'Activity',
      filters: [
        { key: 'EnabledInactiveMembers', label: 'Enabled inactive members', description: `Enabled member accounts inactive for ${threshold}+ days` },
        { key: 'InactiveMembers', label: 'Inactive members', description: `Member accounts inactive for ${threshold}+ days` },
        { key: 'InactiveGuests', label: 'Inactive guests', description: `Guest accounts inactive for ${threshold}+ days` },
      ],
    },
    {
      key: 'attention',
      label: 'Attention',
      badgeKey: 'NeedsAttention',
      filters: [
        { key: 'NeedsAttention', label: 'All issues', description: 'Users with any issue (license waste, no MFA, or inactive)' },
        { key: 'DisabledWithLicenses', label: 'Disabled with licenses', description: 'Disabled accounts still consuming licenses' },
        { key: 'InactiveWithLicenses', label: 'Inactive with licenses', description: `Users inactive for ${threshold}+ days with licenses` },
        { key: 'LicenseWaste', label: 'License waste', description: 'All license optimization opportunities' },
      ],
    },
    {
      key: 'security',
      label: 'Security',
      filters: [
        { key: 'NoMfa', label: 'No MFA', description: 'Users without MFA methods registered' },
        { key: 'NoAuthenticatorApp', label: 'No Authenticator app', description: 'Users without the Authenticator app' },
        { key: 'NoPhishResistantMfa', label: 'No phish-resistant MFA', description: 'Users with MFA but no phish-resistant method (FIDO2, Windows Hello, certificate)' },
        { key: 'HasPhishResistantMfa', label: 'Has phish-resistant MFA', description: 'Users with phish-resistant MFA enrolled' },
      ],
    },
  ]
}

// Map each filter to the tab that contains it
function getTabForFilter(tabs: TabDefinition[], filter: UserIssueFilter): TabKey {
  for (const tab of tabs) {
    if (tab.filters.some(f => f.key === filter)) return tab.key
  }
  return 'all'
}

// Check if the current filter is an inactive-related filter
const isInactiveFilter = (filter: UserIssueFilter): boolean => {
  return filter === 'InactiveWithLicenses' || filter === 'InactiveMembers' || filter === 'InactiveGuests'
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

export default function UserIssuesTable({
  users,
  total,
  currentPage,
  totalPages,
  selectedFilter,
  onFilterChange,
  onPageChange,
  isLoading,
  inactivityThresholdDays,
  tenantId,
  filterCounts,
  resourceAccountCount,
  globalAdminsOnly = false,
  pageSize = 20,
}: Props) {
  const threshold = inactivityThresholdDays ?? DEFAULT_INACTIVITY_THRESHOLD_DAYS
  const tabs = useMemo(() => createTabs(threshold), [threshold])
  const [isExporting, setIsExporting] = useState(false)

  // Active tab tracks which category tab is selected — auto-syncs when filter changes
  const activeTab = useMemo(() => getTabForFilter(tabs, selectedFilter), [tabs, selectedFilter])
  const activeTabDef = tabs.find(t => t.key === activeTab)!

  const needsAttentionCount = filterCounts?.['NeedsAttention'] ?? 0

  // Find the label + description for current filter (for the head + empty state)
  const currentFilterMeta = useMemo(() => {
    for (const tab of tabs) {
      const found = tab.filters.find(f => f.key === selectedFilter)
      if (found) return found
    }
    return null
  }, [tabs, selectedFilter])

  const currentFilterLabel = currentFilterMeta?.label ?? String(selectedFilter)
  const currentFilterDescription = currentFilterMeta?.description ?? ''

  const handleTabClick = (tabKey: TabKey) => {
    const tab = tabs.find(t => t.key === tabKey)
    if (tab) {
      onFilterChange(tab.filters[0].key)
    }
  }

  const exportToCsv = useCallback(async () => {
    if (!tenantId) return
    setIsExporting(true)
    try {
      const pageSize = 100
      // Pass `globalAdminsOnly` through so the SERVER narrows each export
      // page to GAs when the page is GA-scoped. Without this the export
      // would re-fetch the full population and rely on a post-filter that
      // can't see rows beyond what was admitted to the response.
      const firstPage = await api.getUserInsightsUsers(
        tenantId, selectedFilter, 1, pageSize, globalAdminsOnly,
      )
      const allUsers: UserDetailItem[] = [...firstPage.items]
      const totalPages = firstPage.totalPages

      for (let page = 2; page <= totalPages; page++) {
        const result = await api.getUserInsightsUsers(
          tenantId, selectedFilter, page, pageSize, globalAdminsOnly,
        )
        allUsers.push(...result.items)
      }

      // Defense-in-depth: the server-side `globalAdminsOnly` filter is the
      // primary contract, but we keep the post-fetch GA filter as a belt-
      // and-braces guard. Costs nothing on small payloads, protects the
      // export's "this matches the view" promise if a future server change
      // ever weakens the filter.
      const exportRows = globalAdminsOnly
        ? allUsers.filter(u => u.isGlobalAdmin === true)
        : allUsers

      const headers = [
        'Display Name', 'Email', 'Job Title', 'Account Status', 'User Type',
        'Shared Mailbox', 'Resource Account', 'Resource Account Type',
        'Last Sign-In', 'Account Created Date', 'Inactive', 'License Count',
        'Assigned Licenses', 'MFA Enabled', 'Authenticator App',
        'Phish-Resistant MFA', 'FIDO2 Key', 'Windows Hello', 'Certificate Auth',
        'Issue Type',
      ]
      const rows = exportRows.map((user: UserDetailItem) => [
        escapeCsvField(user.displayName || ''),
        escapeCsvField(user.userPrincipalName || ''),
        escapeCsvField(user.jobTitle || ''),
        user.accountEnabled ? 'Enabled' : 'Disabled',
        user.userType || '',
        user.isSharedMailbox ? 'Yes' : 'No',
        user.isResourceAccount ? 'Yes' : 'No',
        user.resourceAccountType || '',
        user.lastSignInDateTime || 'Never',
        user.accountCreatedDate || '',
        user.isInactive ? 'Yes' : 'No',
        String(user.licenseCount ?? 0),
        escapeCsvField(user.assignedLicenses?.join('; ') || ''),
        user.hasMfaEnabled ? 'Yes' : 'No',
        user.hasAuthenticatorApp ? 'Yes' : 'No',
        user.hasPhishResistantMfa ? 'Yes' : 'No',
        user.hasFido2Key ? 'Yes' : 'No',
        user.hasWindowsHello ? 'Yes' : 'No',
        user.hasCertificateAuth ? 'Yes' : 'No',
        user.issueType || '',
      ])
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().split('T')[0]
      const filterLabel = selectedFilter.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '')
      const scopeLabel = globalAdminsOnly ? '-global-admins' : ''
      link.download = `user-directory-${filterLabel}${scopeLabel}-${timestamp}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success(`Exported ${exportRows.length} users to CSV`)
    } catch (error) {
      console.error('CSV export failed:', error)
      toast.error('Failed to export CSV')
    } finally {
      setIsExporting(false)
    }
  }, [tenantId, selectedFilter, globalAdminsOnly])

  const columns: DataColumn<UserDetailItem>[] = [
    {
      id: 'user',
      header: 'User',
      primary: true,
      cell: user => (
        <>
          <TableItem title={user.displayName || user.userPrincipalName} sub={user.userPrincipalName} />
          {user.jobTitle ? <small className="mt-0.5 block text-helper text-text-helper">{user.jobTitle}</small> : null}
        </>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: user => (
        <span className="inline-flex flex-wrap items-center gap-1">
          <Tag kind={user.accountEnabled ? 'default' : 'high-contrast'}>
            {user.accountEnabled ? 'Enabled' : 'Disabled'}
          </Tag>
          {user.userType ? <Tag>{user.userType}</Tag> : null}
          {user.isSharedMailbox && (
            <Tag kind="outline" title="Shared mailbox. Sign-in activity is not expected.">
              Shared
            </Tag>
          )}
          {user.isResourceAccount && (
            <Tag
              kind="outline"
              title={`Resource account: ${user.resourceAccountType ?? 'Unknown'}. Excluded from MFA compliance.`}
            >
              {resourceLabel(user.resourceAccountType)}
            </Tag>
          )}
        </span>
      ),
    },
    {
      id: 'signin',
      header: 'Last sign-in',
      cell: user => (
        <span className="block min-w-0">
          <span className="inline-flex flex-wrap items-center gap-2">
            {user.lastSignInDateTime ? (
              <span className="whitespace-nowrap">{formatDate(user.lastSignInDateTime)}</span>
            ) : (
              <span className="text-text-helper">Never</span>
            )}
            {user.lastSignInDateTime && user.isInactive ? (
              <Tag kind="high-contrast" size="sm">
                Inactive {threshold}+ days
              </Tag>
            ) : null}
          </span>
          {user.accountCreatedDate ? (
            <small
              className="mt-0.5 flex flex-wrap items-center gap-2 text-helper text-text-helper"
              title={new Date(user.accountCreatedDate).toLocaleDateString()}
            >
              Created {formatDate(user.accountCreatedDate).toLowerCase()}
              {isNewAccount(user) ? <Tag size="sm">New</Tag> : null}
            </small>
          ) : null}
        </span>
      ),
    },
    {
      id: 'licenses',
      header: 'Licenses',
      hideBelow: 'c-lg',
      cell: user =>
        user.licenseCount > 0 ? (
          <span className="block min-w-0">
            <span className="block whitespace-nowrap tabular-nums">
              {user.licenseCount} license{user.licenseCount === 1 ? '' : 's'}
            </span>
            <small
              className="mt-0.5 block max-w-[160px] truncate text-helper text-text-helper"
              title={user.assignedLicenses.join(', ')}
            >
              {user.assignedLicenses.slice(0, 2).join(', ')}
              {user.assignedLicenses.length > 2 && ` +${user.assignedLicenses.length - 2}`}
            </small>
          </span>
        ) : (
          <span className="text-text-helper">None</span>
        ),
    },
    {
      id: 'mfa',
      header: 'MFA',
      inlineOnPhone: true,
      cell: user => <MfaCell user={user} />,
    },
    {
      id: 'issue',
      header: 'Issue',
      nowrap: true,
      cell: user => <IssueCell issueType={user.issueType} />,
    },
  ]

  const notices: React.ReactNode[] = []
  if (isInactiveFilter(selectedFilter)) {
    notices.push(
      <InlineNotification key="inactive" kind="info" title="Inactive">
        means no interactive or non-interactive sign-in in the last {threshold} days. Shared mailboxes are
        expected to show as inactive since they don&rsquo;t have direct sign-ins.
      </InlineNotification>,
    )
  }
  if (selectedFilter === 'ActiveMembers' || selectedFilter === 'ActiveGuests') {
    notices.push(
      <InlineNotification key="active" kind="info" title="Active">
        means the account is enabled and has sign-in activity within the last {threshold} days.
      </InlineNotification>,
    )
  }
  if ((resourceAccountCount ?? 0) > 0) {
    notices.push(
      <InlineNotification
        key="resource"
        kind="info"
        title={`${(resourceAccountCount ?? 0).toLocaleString()} resource accounts`}
        actions={
          <Button kind="ghost" size="sm" onClick={() => onFilterChange('ResourceAccounts')}>
            View resource accounts
          </Button>
        }
      >
        (room mailboxes, equipment, Bookings, Teams Phone and Rooms) are left out of MFA adoption and
        license waste. They use service-level or free licenses and don&rsquo;t support interactive MFA.
      </InlineNotification>,
    )
  }

  const filterOptions = activeTabDef.filters.map(filter => {
    const count = filterCounts?.[filter.key]
    return {
      value: filter.key,
      label: count !== undefined && count > 0 ? `${filter.label} (${count.toLocaleString()})` : filter.label,
    }
  })

  return (
    <DataTable
      title="User directory"
      description={
        <>
          <b className="font-semibold tabular-nums text-text-primary">{total.toLocaleString()}</b>{' '}
          {total === 1 ? 'person' : 'people'} · {currentFilterDescription || currentFilterLabel}
        </>
      }
      notices={notices.length > 0 ? notices : undefined}
      tabs={
        <ContainedTabs
          aria-label="User categories"
          value={activeTab}
          onChange={id => handleTabClick(id as TabKey)}
          tabs={tabs.map(tab => {
            const badgeCount = tab.badgeKey ? filterCounts?.[tab.badgeKey] : undefined
            return {
              id: tab.key,
              label: tab.label,
              count: badgeCount !== undefined && badgeCount > 0 ? badgeCount.toLocaleString() : null,
            }
          })}
        />
      }
      toolbar={
        <>
          <TableSelect
            label="Show"
            value={selectedFilter}
            options={filterOptions}
            onChange={value => onFilterChange(value as UserIssueFilter)}
          />
          <div className="ml-auto flex flex-none items-stretch max-c-md:ml-0">
            {needsAttentionCount > 0 && activeTab !== 'attention' && (
              <Button kind="ghost" onClick={() => onFilterChange('NeedsAttention')}>
                {needsAttentionCount.toLocaleString()} need attention
              </Button>
            )}
            {tenantId && total > 0 && (
              <Button
                kind="ghost"
                onClick={exportToCsv}
                loading={isExporting}
                icon={<IconDownload />}
              >
                {isExporting ? 'Exporting…' : 'Export CSV'}
              </Button>
            )}
          </div>
        </>
      }
      columns={columns}
      rows={users}
      rowId={user => user.userId}
      rowLabel={user => user.displayName || user.userPrincipalName}
      isLoading={isLoading}
      empty={
        selectedFilter === 'All'
          ? 'No user data available. Run a scan to collect user information.'
          : 'No one matches this filter. Try a different filter, or run a scan to refresh the data.'
      }
      pagination={
        total > 0
          ? {
              page: currentPage,
              pageSize,
              total,
              totalPages: Math.max(1, totalPages),
              onPageChange,
              itemLabel: total === 1 ? 'person' : 'people',
              isLoading,
            }
          : undefined
      }
    />
  )
}

/* -------------------------------------------------------------------------- */
/* Cells                                                                      */
/* -------------------------------------------------------------------------- */

/** MFA strength in words: phish-resistant (with its methods), standard, or none. */
function MfaCell({ user }: { user: UserDetailItem }) {
  const authApp = user.hasAuthenticatorApp ? (
    <Tag size="sm" title="Microsoft Authenticator app">
      Auth app
    </Tag>
  ) : null

  if (user.hasPhishResistantMfa) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span className="whitespace-nowrap text-text-primary">Phish-resistant</span>
        {user.hasFido2Key && (
          <Tag size="sm" title="FIDO2 security key">
            FIDO2
          </Tag>
        )}
        {user.hasWindowsHello && (
          <Tag size="sm" title="Windows Hello for Business">
            WHfB
          </Tag>
        )}
        {user.hasCertificateAuth && (
          <Tag size="sm" title="Certificate-based authentication">
            Cert
          </Tag>
        )}
        {authApp}
      </span>
    )
  }
  if (user.hasMfaEnabled) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5" title="Standard MFA only. Not phish-resistant.">
        <span className="whitespace-nowrap">Standard MFA</span>
        {authApp}
      </span>
    )
  }
  return (
    <b className="font-semibold text-text-primary" title="No MFA methods registered">
      None
    </b>
  )
}

/** The issue: severity by shape and word (Coral for critical only), or plain text when it isn't an issue. */
function IssueCell({ issueType }: { issueType: string }) {
  const level = getIssueLevel(issueType)
  const label = getIssueLabel(issueType)
  if (!level) return <span className="text-text-helper">{label}</span>
  return <SeverityGlyph level={level}>{label}</SeverityGlyph>
}

/* -------------------------------------------------------------------------- */
/* Helpers — kept identical to legacy (only callers shape changed)            */
/* -------------------------------------------------------------------------- */

function resourceLabel(type: string | undefined): string {
  switch (type) {
    case 'RoomMailbox': return 'Room'
    case 'EquipmentMailbox': return 'Equipment'
    case 'SchedulingMailbox': return 'Bookings'
    case 'TeamsPhone': return 'Teams Phone'
    case 'TeamsRooms': return 'Teams Room'
    default: return 'Resource'
  }
}

// Accounts created within the last 90 days that have never signed in
// are flagged as "New Account" to distinguish them from truly inactive users
function isNewAccount(user: UserDetailItem): boolean {
  if (!user.accountCreatedDate || user.lastSignInDateTime) return false
  const created = new Date(user.accountCreatedDate)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))
  return diffDays <= 90
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`

  return date.toLocaleDateString()
}

function getIssueLabel(issueType: string): string {
  if (!issueType) return 'No issues'
  switch (issueType) {
    case 'DisabledWithLicenses': return 'Disabled with licenses'
    case 'InactiveWithLicenses': return 'Inactive with licenses'
    case 'InactiveMembers': return 'Inactive member'
    case 'InactiveGuests': return 'Inactive guest'
    case 'NoMfa': return 'No MFA'
    case 'NoAuthenticatorApp': return 'No Authenticator app'
    case 'NoPhishResistantMfa': return 'No phish-resistant MFA'
    case 'HasPhishResistantMfa': return 'Phish-resistant'
    case 'LicenseWaste': return 'License waste'
    default: return issueType
  }
}

/** The issue's severity level, or null when the row is not an open issue. */
function getIssueLevel(issueType: string): SeverityLevel | null {
  if (!issueType) return null
  switch (issueType) {
    case 'DisabledWithLicenses':
    case 'NoMfa':
      return 'crit'
    case 'InactiveWithLicenses':
    case 'InactiveMembers':
    case 'NoAuthenticatorApp':
    case 'NoPhishResistantMfa':
    case 'LicenseWaste':
      return 'high'
    case 'InactiveGuests':
      return 'med'
    case 'HasPhishResistantMfa':
    default:
      return null
  }
}
